import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { EVENT_TYPES } from '@/shared/constants/events';
import {
	DeclarationsTrackingContext,
	definedProps,
	newScreenVisit,
	ScreenVisitContext,
	sectionsSeenOf,
	takePreviousSurface,
	type ScreenVisit,
	type TrackingProps,
} from '../lib/tracking';
import { secondsSince } from '../lib/readingTime';
import { useDeclarationsTrack, useTrackOnce } from '../hooks/useDeclarationsTrack';

const ViewTracker = ({ surface, screen }: { surface: string; screen: RefObject<ScreenVisit | null> }) => {
	const [fromSurface] = useState(() => takePreviousSurface(surface));
	useTrackOnce(EVENT_TYPES.DECLARATIONS_VIEWED, { from_surface: fromSurface });
	const track = useDeclarationsTrack();
	const latestTrack = useRef(track);
	useEffect(() => {
		latestTrack.current = track;
	}, [track]);
	useEffect(() => {
		const visit = screen.current ?? newScreenVisit();
		let left = false;
		const leave = (how: 'navigate' | 'hidden') => {
			if (left) return;
			left = true;
			latestTrack.current(EVENT_TYPES.DECLARATIONS_SCREEN_LEFT, {
				seconds_on_screen: secondsSince(visit.shownAt),
				how,
				...sectionsSeenOf(visit),
			});
		};
		const onVisibility = () => {
			if (document.visibilityState === 'hidden') leave('hidden');
			else left = false;
		};
		document.addEventListener('visibilitychange', onVisibility);
		return () => {
			document.removeEventListener('visibilitychange', onVisibility);
			leave('navigate');
		};
	}, [screen]);
	return null;
};

type TrackingScopeProps = {
	value: TrackingProps;
	view?: boolean;
	children: ReactNode;
};

const TrackingScope = ({ value, view = false, children }: TrackingScopeProps) => {
	const parent = useContext(DeclarationsTrackingContext);
	const parentScreen = useContext(ScreenVisitContext);
	const ownScreen = useRef<ScreenVisit | null>(null);
	const key = JSON.stringify(definedProps(value));
	const merged = useMemo(() => ({ ...parent, ...(JSON.parse(key) as TrackingProps) }), [parent, key]);
	const surface = typeof merged.surface === 'string' ? merged.surface : undefined;
	const viewKey = view && surface ? `${surface}-${merged.year ?? ''}-${merged.period ?? ''}-${merged.regime ?? ''}` : undefined;
	useLayoutEffect(() => {
		if (viewKey) ownScreen.current = newScreenVisit();
	}, [viewKey]);
	return (
		<DeclarationsTrackingContext.Provider value={merged}>
			<ScreenVisitContext.Provider value={viewKey ? ownScreen : parentScreen}>
				{viewKey && surface && (
					<ViewTracker
						key={viewKey}
						surface={surface}
						screen={ownScreen}
					/>
				)}
				{children}
			</ScreenVisitContext.Provider>
		</DeclarationsTrackingContext.Provider>
	);
};

export default TrackingScope;
